import {Portion} from './portion.model';
import {Product} from './product.model';
import {Energy} from './energy.model';
import md5 from 'locutus/php/strings/md5';
import {Weight} from "./weight.model";
import {CARBO, FIBER, GLICEMIC_INDEX, GLICEMIC_LOAD} from "../services/NutrientDetailsProvider";

export class Food {

  constructor(
      id,
      externalId,
      name,
      portions,
      parentId,
      hash,
      products = [],
      portion,
      recipe,
      tags = [],
      diets = [],
      equipments = [],
      meals = [],
      repeating = []
      ) {
    this._id = id;
    this._externalId = externalId;
    this._name = name;
    this._portions = portions;
    this._parentId = parentId;
    this._hash = hash;
    this._products = products;
    this._portion = portion;
    this._recipe = recipe;
    this._tags = tags;
    this._diets = diets;
    this._equipments = equipments;
    this._meals = meals;
    this._repeating = repeating;
    this._date = null;
  }

  get id() {
    return this._id;
  }

  get externalId() {
    return this._externalId;
  }

  set externalId(id) {
    this._externalId = id;
  }

  get name() {
    return this._name;
  }

  set name(name) {
    this._name = name;
  }

  get portions() {
    return this._portions;
  }

  set portions(portions) {
    this._portions = portions;
  }

  get parentId() {
    return this._parentId;
  }

  set parentId(parentId) {
    this._parentId = parentId;
  }

  get hash() {
    return this._hash;
  }

  set date(date) {
    this._date = Number(date);
  }

  get date() {
    return this._date;
  }

  addProduct(newProduct) {
    if (!(newProduct instanceof Product)) {
      throw new Error('Unsupported type');
    }

    const product = this.findProductByIdPortion(newProduct.externalId, newProduct.portion);
    if (product) {
      product.portion.add(newProduct.portion);
      return;
    }

    this._products.push(newProduct);
    this.regenerateHash();
  }

  findProduct(id, key = 'externalId') {
    return this._products.find((p) => p[key] === id);
  }

  findProductsBy(value, key) {
    return this._products.filter((product) => product[key] === value);
  }

  findProductByIdPortion(externalId, portion) {
    return this._products.find(p => p.externalId === externalId && p.portion.isEqual(portion));
  }

  removeProduct(id) {
    this._products = this._products.filter((p) => p.id !== id);
    this.regenerateHash();
  }

  replaceProduct(id, productReplacement) {
    const productByPortion = this.findProductByIdPortion(productReplacement.externalId, productReplacement.portion);
    if (productByPortion && productByPortion.id !== id) {
      this.removeProduct(id);
      this.addProduct(productReplacement);
    } else if (typeof productByPortion === 'undefined'){
      const productIndex = this._products.findIndex((product) => product.id === id);
      if (productIndex !== -1) {
        const products = [...this._products];
        products[productIndex] = productReplacement;
        this._products = products;
      }
    }
    this.regenerateHash();
  }

  get products() {
    return this._products;
  }

  set products(products) {
    this._products = products;
  }

  get portion() {
    return this._portion;
  }

  set portion(portion) {
    if (!(portion instanceof Portion)) {
      throw new Error('Portion has to be instance of Portion');
    }

    this._portion = portion;
  }

  get recipe() {
    return this._recipe;
  }

  set recipe(recipe) {
    this._recipe = recipe;
  }

  get tags() {
    return this._tags;
  }

  set tags(tags) {
    this._tags = tags;
  }

  get diets() {
    return this._diets;
  }

  set diets(diets) {
    this._diets = diets;
  }

  get equipments() {
    return this._equipments;
  }

  set equipments(equipments) {
    this._equipments = equipments;
  }

  get meals() {
    return this._meals;
  }

  set meals(meals) {
    this._meals = meals;
  }

  get repeating() {
    return this._repeating;
  }

  set repeating(dates) {
    this._repeating = dates;
  }

  hasDisliked() {
    return this._products.filter(p => p.disliked).length > 0;
  }

  hasAllergens() {
    return this._products.filter(p => p.allergen).length > 0;
  }

  nutrients(forSinglePortion = false) {
    const total = {};

    const foodPortion = this.portion.amount;

    this.products.forEach((product) => {
      const factor = product.portion.getFactor() * foodPortion;

      // those are indexes, which cannot be summed.
      const ignored = [GLICEMIC_INDEX, GLICEMIC_LOAD];
      for (const externalId in product.nutrients) {
        if (ignored.includes(Number(externalId))) {
          return;
        }
        let nutrient = product.nutrients[externalId];

        if (forSinglePortion) {
          nutrient = nutrient / this._portions;
        }

        if (!total[externalId]) {
          total[externalId] = 0;
        }

        total[externalId] += nutrient * factor;
      }
    });

    return total;
  }

  calculateGlicemicLoad(forSinglePortion = true) {
    const totalCarbohydrates = this.sumNutrientById(CARBO, forSinglePortion);
    const totalFiber = this.sumNutrientById(FIBER, forSinglePortion);
    const glicemicIndex = this.calculateGlicemicIndex(forSinglePortion);
    return ((totalCarbohydrates - totalFiber) * glicemicIndex) / 100;
  }

  calculateGlicemicIndex(forSinglePortion = true) {
    const totalCarbohydrates = this.sumNutrientById(CARBO, forSinglePortion);
    const totalFiber = this.sumNutrientById(FIBER, forSinglePortion);
    const totalDigestibleCarbohydrates = totalCarbohydrates - totalFiber;

    if (totalDigestibleCarbohydrates === 0) {
      return 0;
    }

    let totalGlicemicIndex = 0;

    this._products.forEach((p) => {
      const productGlicemicIndex = p.getGlicemicIndex();
      let productCarbohydrates = p.getCarbohydrates();
      let productFiber = p.getFiber();

      if (forSinglePortion) {
        productCarbohydrates = productCarbohydrates / this.portions * this.portion.amount;
        productFiber = productFiber / this.portions * this.portion.amount;
      }

      const percentage = (productCarbohydrates - productFiber) / (totalDigestibleCarbohydrates);

      totalGlicemicIndex += percentage * productGlicemicIndex;
    });

    return totalGlicemicIndex;
  }

  sumNutrientById(id, forSinglePortion = true) {
    let neutrientSum = this._products
      .map((p) => p.sumNutrientById(id))
      .reduce((p, t) => p + t, 0);

    if (forSinglePortion) {
      neutrientSum = neutrientSum / this._portions;
    }

    return neutrientSum * this.portion.amount;
  }

  calculateEnergy(forSinglePortion = false) {
    let total = this._products
        .map((p) => p.energyInPortion.amount)
        .reduce((p, c) => p + c, 0);
    if (forSinglePortion === false) {
      total = total / this._portions;
    }

    return new Energy(total * this.portion.amount);
  }

  regenerateHash() {
    if (this._products.length === 0) {
      return;
    }

    const products = this._products.slice(0);

    let hashValues = products.sort((a, b) => a.name.localeCompare(b.name)).map(p => p.externalId);

    hashValues.push(this.isMultiPortionFood());

    this._hash = md5(
      JSON.stringify(hashValues)
    );
  }

  isMultiPortionFood() {
    return this._portions > 1;
  }

  getTotalWeight() {
    const amount = this._products.reduce((total, product) => {
      return total + product.getTotalWeight().amount;
    }, 0);

    return new Weight(amount, 'gram');
  }

  isValid() {
    return this._name !== null &&
        this._name.trim().length > 0 &&
        this._recipe !== null &&
        this._recipe.trim().length > 0 &&
        this._products.length > 0;
  }

  toJSON() {
    return {
      id: this.id,
      externalId: this.externalId,
      name: this.name,
      portions: this.portions,
      energy: this.calculateEnergy(),
      products: this.products,
      portion: this.portion,
      tags: this.tags,
      diets: this.diets,
      equipments: this.equipments,
      recipe: this.recipe,
      meals: this.meals,
      repeating: this.repeating,
      parentId: this.parentId,
      hash: this.hash,
    };
  }
}
